import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuthStore } from '../../store/authStore';
import {
  X, User, Palette, Link2, ShieldCheck, LogOut, Loader2,
  Camera, Type, Check, Pencil, Upload, Code, Play, Wallet, ExternalLink, Monitor,
} from 'lucide-react';
import AccountConnectionsTab from './AccountConnectionsTab';
import AccountStatusTab from './AccountStatusTab';
import BillingSettingsTab from './BillingSettingsTab';
import { uploadAPI } from '../../lib/api';
import { setDeveloperModeEnabled, useDeveloperMode } from '../../lib/developerMode';
import {
  getScreenShareEncoding,
  getScreenShareSettings,
  SCREEN_SHARE_FPS_PRESETS,
  SCREEN_SHARE_RESOLUTION_PRESETS,
  setScreenShareSettings,
  type ScreenShareFpsPreset,
  type ScreenShareResolutionPreset,
} from '../../lib/screenShareSettings';

interface UserSettingsModalProps {
  onClose: () => void;
  initialTab?: string;
}

/* ─── Tab / category definitions ─── */
interface Tab { id: string; label: string; icon: React.ElementType }
interface Category { label: string; tabs: Tab[] }

const CATEGORIES: Category[] = [
  {
    label: 'Compte',
    tabs: [
      { id: 'account', label: 'Mon compte', icon: User },
      { id: 'connections', label: 'Connexions', icon: Link2 },
      { id: 'account_status', label: 'Statut du compte', icon: ShieldCheck },
    ],
  },
  {
    label: 'Facturation',
    tabs: [
      { id: 'billing', label: 'Facturation', icon: Wallet },
    ],
  },
  {
    label: 'Application',
    tabs: [
      { id: 'appearance', label: 'Apparence', icon: Palette },
      { id: 'voice_video', label: 'Voice & Video', icon: Monitor },
    ],
  },
  {
    label: 'Développeur',
    tabs: [
      { id: 'developer', label: 'Outils dev', icon: Code },
    ],
  },
];

/* ─── Themes ─── */
const THEMES = [
  {
    id: 'dark',
    label: 'Sombre',
    bg: 'bg-[#0F0F0F]',
    bar: 'bg-[#1A1A1A]',
    border: 'border-[#333]',
    text: 'text-white',
  },
  {
    id: 'light',
    label: 'Clair',
    bg: 'bg-white',
    bar: 'bg-gray-100',
    border: 'border-gray-300',
    text: 'text-gray-800',
  },
  {
    id: 'midnight',
    label: 'Midnight',
    bg: 'bg-blue-950',
    bar: 'bg-blue-900',
    border: 'border-blue-800',
    text: 'text-white',
  },
];

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({ onClose, initialTab }) => {
  const { profile, updateProfile, signOut } = useAuthStore();
  const [activeTab, setActiveTab] = useState<string>(initialTab || 'account');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [bio, setBio] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingBanner, setEditingBanner] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [screenShareSettings, setScreenShareSettingsState] = useState(() => getScreenShareSettings());
  const developerMode = useDeveloperMode();
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    setError(null);
    try {
      const { url } = await uploadAPI.uploadAvatar(file);
      setAvatarUrl(url);
      await updateProfile({ avatar_url: url });
      setSuccess('Avatar mis à jour');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Échec de l\'upload');
    } finally {
      setUploadingAvatar(false);
      if (avatarFileRef.current) avatarFileRef.current.value = '';
    }
  };

  const handleBannerFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    setError(null);
    try {
      const { url } = await uploadAPI.uploadBanner(file);
      setBannerUrl(url);
      await updateProfile({ banner_url: url });
      setSuccess('Bannière mise à jour');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Échec de l\'upload');
    } finally {
      setUploadingBanner(false);
      if (bannerFileRef.current) bannerFileRef.current.value = '';
    }
  };

  const updateScreenShareSettings = (updates: Partial<typeof screenShareSettings>) => {
    const next = setScreenShareSettings({ ...screenShareSettings, ...updates });
    setScreenShareSettingsState(next);
  };

  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setDisplayName(profile.display_name || '');
      setAvatarUrl(profile.avatar_url || '');
      setCustomStatus(profile.custom_status || '');
      setBio(profile.bio || '');
      setBannerUrl(profile.banner_url || '');
    }
  }, [profile]);

  /* ESC to close */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await updateProfile({
        display_name: displayName || null,
        avatar_url: avatarUrl,
        custom_status: customStatus,
        bio,
        banner_url: bannerUrl,
      });
      setSuccess('Profil mis à jour avec succès');
      setEditingBanner(false);
      setEditingAvatar(false);
    } catch (err: any) {
      setError(err.message || 'Échec de la mise à jour');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  /* ════════ Tab renderers ════════ */
  const renderAccount = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Mon compte</h2>
        <p className="text-xs text-text-muted mt-0.5">Gérez votre profil et vos informations personnelles.</p>
      </div>

      {/* Profile card — interactive banner & avatar */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl overflow-hidden">
        {/* Banner — clickable to upload */}
        <input ref={bannerFileRef} type="file" accept="image/*" className="hidden" onChange={handleBannerFile} />
        <div
          className="relative h-28 group cursor-pointer"
          onClick={() => bannerFileRef.current?.click()}
        >
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-accent-color/30 via-accent-color/10 to-transparent" />
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
            {uploadingBanner ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-white text-xs font-medium">
                <Upload className="w-4 h-4" />
                {bannerUrl ? 'Changer la bannière' : 'Ajouter une bannière'}
              </div>
            )}
          </div>
        </div>

        {/* Identity row */}
        <div className="px-5 pb-5 -mt-8">
          <div className="flex items-end gap-4">
            {/* Avatar — clickable to upload */}
            <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarFile} />
            <div
              className="relative w-16 h-16 rounded-xl bg-bg-tertiary border-4 border-bg-secondary overflow-hidden flex-shrink-0 shadow-lg group cursor-pointer"
              onClick={() => avatarFileRef.current?.click()}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xl font-black text-text-muted">
                  {profile?.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center">
                {uploadingAvatar ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>
            </div>
            <div className="pb-1 min-w-0">
              <p className="text-base font-bold text-text-primary truncate">{profile?.username}</p>
              <p className="text-xs text-text-muted truncate">{profile?.custom_status || 'Aucun statut'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Pencil className="w-3.5 h-3.5 text-accent-color" /> Modifier le profil
        </h3>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs mb-4">
            <X className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/30 text-success text-xs mb-4">
            <Check className="w-3.5 h-3.5 flex-shrink-0" /> {success}
          </div>
        )}

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <Field label="Nom d'utilisateur" htmlFor="settings-username">
            <div className="flex items-center gap-3">
              <input
                id="settings-username"
                type="text"
                value={username}
                disabled
                className="settings-input opacity-60 cursor-not-allowed flex-1"
              />
              <a
                href="https://accounts.epivalent.com/gaming-profile"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent-color/10 border border-accent-color/30 text-xs font-medium text-accent-color hover:bg-accent-color/20 transition-all whitespace-nowrap"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Modifier
              </a>
            </div>
            <p className="text-[10px] text-text-muted mt-1">Pour changer votre nom d'utilisateur, rendez-vous sur votre profil Epivalent.</p>
          </Field>

          <Field label="Nom d'affichage" htmlFor="settings-displayname">
            <input
              id="settings-displayname"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="settings-input"
              placeholder="Votre nom d'affichage (optionnel)"
            />
            <p className="text-[10px] text-text-muted mt-1">Ce nom sera affiché à la place de votre nom d'utilisateur.</p>
          </Field>

          {/* Avatar — file upload */}
          <Field label="Photo de profil" htmlFor="settings-avatar-upload">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-bg-tertiary border border-border-color overflow-hidden flex-shrink-0">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm font-bold text-text-muted">
                    {profile?.username?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <button
                type="button"
                id="settings-avatar-upload"
                onClick={() => avatarFileRef.current?.click()}
                disabled={uploadingAvatar}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-tertiary border border-border-color text-xs font-medium text-text-secondary hover:text-text-primary hover:border-accent-color/50 transition-all disabled:opacity-50"
              >
                {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploadingAvatar ? 'Upload...' : 'Changer l\'avatar'}
              </button>
            </div>
          </Field>

          {/* Banner — file upload */}
          <Field label="Bannière" htmlFor="settings-banner-upload">
            <div className="space-y-2">
              <button
                type="button"
                id="settings-banner-upload"
                onClick={() => bannerFileRef.current?.click()}
                disabled={uploadingBanner}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-tertiary border border-border-color text-xs font-medium text-text-secondary hover:text-text-primary hover:border-accent-color/50 transition-all disabled:opacity-50"
              >
                {uploadingBanner ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploadingBanner ? 'Upload...' : 'Changer la bannière'}
              </button>
              {bannerUrl && (
                <div className="h-16 rounded-lg overflow-hidden border border-border-color">
                  <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </Field>

          {/* Bio / À propos */}
          <Field label="À propos de moi" htmlFor="settings-bio">
            <textarea
              id="settings-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="settings-input min-h-[80px] resize-y"
              placeholder="Parlez de vous..."
              maxLength={500}
              rows={3}
            />
            <p className="text-[10px] text-text-muted mt-1 text-right">{bio.length}/500</p>
          </Field>

          <Field label="Statut personnalisé" htmlFor="settings-status">
            <input
              id="settings-status"
              type="text"
              value={customStatus}
              onChange={(e) => setCustomStatus(e.target.value)}
              className="settings-input"
              placeholder="Que faites-vous ?"
            />
          </Field>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {isLoading ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderAppearance = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Apparence</h2>
        <p className="text-xs text-text-muted mt-0.5">Personnalisez le look d'Erozion.</p>
      </div>

      {/* Theme picker */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4 text-accent-color" /> Thème
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {THEMES.map((t) => {
            const active = t.id === 'dark'; // currently dark is active
            return (
              <button
                key={t.id}
                className={`rounded-xl overflow-hidden border-2 transition-all ${
                  active ? 'border-accent-color ring-2 ring-accent-color/20' : 'border-border-color hover:border-text-muted/40'
                }`}
              >
                <div className={`h-14 ${t.bg}`} />
                <div className={`h-8 ${t.bar} border-t ${t.border} flex items-center justify-center`}>
                  <span className={`text-xs font-medium ${t.text}`}>{t.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Font size */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Type className="w-4 h-4 text-accent-color" /> Taille de police
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">Aa</span>
          <input
            type="range"
            min="12"
            max="20"
            defaultValue="16"
            className="flex-1 h-1.5 bg-bg-tertiary rounded-full appearance-none cursor-pointer accent-accent-color"
          />
          <span className="text-base text-text-muted font-bold">Aa</span>
        </div>
      </div>
    </div>
  );

  const renderVoiceVideo = () => {
    const encoding = getScreenShareEncoding(screenShareSettings);
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Voice & Video</h2>
          <p className="text-xs text-text-muted mt-0.5">Configure screen sharing quality for this device.</p>
        </div>

        <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
          <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-accent-color" /> Screen Share Quality
          </h3>

          <div className="space-y-5">
            <div>
              <p className="text-xs font-bold text-text-secondary uppercase tracking-[0.12em] mb-2">Resolution</p>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(SCREEN_SHARE_RESOLUTION_PRESETS) as ScreenShareResolutionPreset[]).map((resolution) => {
                  const preset = SCREEN_SHARE_RESOLUTION_PRESETS[resolution];
                  const active = screenShareSettings.resolution === resolution;
                  return (
                    <button
                      key={resolution}
                      type="button"
                      onClick={() => updateScreenShareSettings({ resolution })}
                      className={`rounded-xl border p-3 text-left transition-all ${
                        active
                          ? 'border-accent-color bg-accent-color/10 ring-2 ring-accent-color/15'
                          : 'border-border-color bg-bg-tertiary/60 hover:border-text-muted/40 hover:bg-bg-tertiary'
                      }`}
                    >
                      <p className="text-sm font-bold text-text-primary">{preset.label}</p>
                      <p className="text-[10px] text-text-muted mt-0.5">{preset.width} x {preset.height}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold text-text-secondary uppercase tracking-[0.12em] mb-2">Frame rate</p>
              <div className="grid grid-cols-3 gap-2">
                {SCREEN_SHARE_FPS_PRESETS.map((fps: ScreenShareFpsPreset) => {
                  const active = screenShareSettings.fps === fps;
                  return (
                    <button
                      key={fps}
                      type="button"
                      onClick={() => updateScreenShareSettings({ fps })}
                      className={`rounded-xl border px-3 py-2.5 text-center transition-all ${
                        active
                          ? 'border-accent-color bg-accent-color/10 ring-2 ring-accent-color/15'
                          : 'border-border-color bg-bg-tertiary/60 hover:border-text-muted/40 hover:bg-bg-tertiary'
                      }`}
                    >
                      <span className="text-sm font-bold text-text-primary">{fps}</span>
                      <span className="ml-1 text-[10px] text-text-muted">fps</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-border-color bg-bg-primary/50 p-3">
              <p className="text-xs text-text-secondary">
                Current target: <span className="font-semibold text-text-primary">{screenShareSettings.resolution}</span> at{' '}
                <span className="font-semibold text-text-primary">{screenShareSettings.fps} fps</span>, up to{' '}
                <span className="font-semibold text-text-primary">{Math.round(encoding.maxBitrate / 1_000_000)} Mbps</span>.
              </p>
              <p className="text-[10px] text-text-muted mt-1">
                Higher settings use more bandwidth and CPU. Browser, monitor, network, or LiveKit conditions may cap the actual quality.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isElectron = !!(window as any).api;

  const renderDeveloper = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Outils développeur</h2>
        <p className="text-xs text-text-muted mt-0.5">Outils internes pour le développement et le debugging.</p>
      </div>

      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Code className="w-3.5 h-3.5 text-accent-color" /> Mode développeur
        </h3>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-text-primary font-medium">Afficher les IDs</p>
            <p className="text-xs text-text-muted">Ajoute les options de copie d'ID dans les menus des guilds, canaux, utilisateurs et messages.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={developerMode}
            onClick={() => setDeveloperModeEnabled(!developerMode)}
            className={`relative h-6 w-11 rounded-full border transition-all ${
              developerMode
                ? 'bg-accent-color border-accent-color'
                : 'bg-bg-tertiary border-border-color'
            }`}
          >
            <span
              className={`absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform ${
                developerMode ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Code className="w-3.5 h-3.5 text-accent-color" /> Mise à jour
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-primary font-medium">Simuler une mise à jour</p>
              <p className="text-xs text-text-muted">Lance une fausse mise à jour de 60 secondes pour tester l'UI de mise à jour fullscreen.</p>
            </div>
            <button
              onClick={() => { (window as any).api?.simulateUpdate(); onClose(); }}
              disabled={!isElectron}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" />
              Simuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'account':
        return renderAccount();
      case 'appearance':
        return renderAppearance();
      case 'voice_video':
        return renderVoiceVideo();
      case 'connections':
        return <AccountConnectionsTab />;
      case 'account_status':
        return <AccountStatusTab />;
      case 'billing':
        return <BillingSettingsTab />;
      case 'developer':
        return renderDeveloper();
      default:
        return null;
    }
  }, [activeTab, username, displayName, avatarUrl, customStatus, bio, bannerUrl, editingBanner, editingAvatar, isLoading, error, success, profile, developerMode, screenShareSettings]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-bg-primary rounded-2xl w-[95vw] max-w-[80rem] h-[92vh] flex overflow-hidden border border-border-color shadow-2xl shadow-black/50 animate-slide-up">
        {/* ── Sidebar ── */}
        <aside className="w-56 flex-shrink-0 bg-bg-secondary/60 border-r border-border-color flex flex-col">
          <div className="flex-1 overflow-y-auto p-3 gaming-scrollbar">
            <h2 className="px-3 pt-2 pb-4 text-[13px] font-bold text-text-primary">
              Settings
            </h2>

            {CATEGORIES.map((cat) => (
              <div key={cat.label} className="mb-4">
                <p className="px-3 mb-1 text-[10px] font-bold text-text-muted uppercase tracking-[0.12em]">
                  {cat.label}
                </p>
                <div className="space-y-0.5">
                  {cat.tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                          isActive
                            ? 'bg-accent-color/10 text-accent-color'
                            : 'text-text-secondary hover:text-text-primary hover:bg-highlight/50'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Sign-out */}
          <div className="p-3 border-t border-border-color">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-error hover:bg-error/10 transition-all"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </button>
          </div>
        </aside>

        {/* ── Content ── */}
        <main className="flex-1 overflow-y-auto p-8 gaming-scrollbar">
          {renderTabContent()}
        </main>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-bg-tertiary/80 border border-border-color flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight transition-all"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  , document.body);
};

/* ─── Tiny helper ─── */
const Field: React.FC<{ label: string; htmlFor: string; children: React.ReactNode }> = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-semibold text-text-secondary mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

export default UserSettingsModal;
